import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Stack,
    Divider,
    useTheme,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BoltIcon from '@mui/icons-material/Bolt';
import VerifiedIcon from '@mui/icons-material/Verified';
import HandshakeIcon from '@mui/icons-material/Handshake';

import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

interface HomePageProps {
    mode: 'light' | 'dark';
    setMode: (mode: 'light' | 'dark') => void;
}

const AboutPage = ({ mode, setMode }: HomePageProps) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [isLoggedIn, setLoggedin] = useState<boolean>(false);

    return (
        <>
            <Header isLoggedIn={isLoggedIn} mode={mode} setMode={setMode} />

            {/* HERO SECTION */}
            <Box
                sx={{
                    backgroundColor:
                        theme.palette.mode === 'light'
                            ? '#E3F2FD'
                            : theme.palette.background.paper,
                    py: { xs: 10, sm: 14 },
                    textAlign: 'center',
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        fontWeight={800}
                        gutterBottom
                        sx={{ color: 'text.primary' }}
                    >
                        Your Partner for Commercial Vehicles
                    </Typography>

                    <Typography variant="h5" color="text.secondary" mb={4}>
                        Connecting Buyers, Dealers & Businesses
                        <br />
                        Empowering the industry with trusted listings, verified dealers, and
                        data-driven tools for smarter decisions.
                    </Typography>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="center"
                        mt={3}
                    >
                        <Button
                            variant="contained"
                            size="large"
                            sx={{ bgcolor: '#ffce00', color: '#000' }}
                            onClick={() => navigate('/vehicle-listing')}
                        >
                            Explore Listings
                        </Button>

                        <Button
                            variant="outlined"
                            size="large"
                            color="primary"
                            component="a"
                            href="https://consumerstaging.workhorse.com.sg/partner-with-us/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Partner with Us
                        </Button>
                    </Stack>
                </Container>
            </Box>

            {/* ABOUT SECTION */}
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    About WorkHorse
                </Typography>

                <Typography variant="body1" color="text.secondary" mb={4}>
                    WorkHorse is Singapore’s specialised commercial vehicle platform built for
                    dealers, businesses, and buyers who want a faster, safer, and more
                    efficient way to buy and sell commercial vehicles.
                    <br />
                    Whether you’re managing a single van or a full fleet, WorkHorse delivers
                    a seamless experience designed to save time, reduce operational hassle,
                    and achieve stronger results.
                </Typography>

                <Divider sx={{ my: 4 }} />

                {/* DIFFERENT SECTION */}
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    What Makes Us Different
                </Typography>

                <Grid container spacing={4} mt={1}>
                    {/* ITEM 1 */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box sx={{ fontSize: 40, color: theme.palette.primary.main }}>
                                <DirectionsCarIcon fontSize="inherit" />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Exclusively for Commercial Vehicles
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    No passenger cars. Only vans, lorries, trucks, pickups, buses
                                    & fleet vehicles.
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* ITEM 2 */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box sx={{ fontSize: 40, color: theme.palette.primary.main }}>
                                <PeopleAltIcon fontSize="inherit" />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Built for Dealers, Businesses & Professional Buyers
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Fast listings, verified dealers, organised categories —
                                    designed for real industry needs.
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* ITEM 3 */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box sx={{ fontSize: 40, color: theme.palette.warning.main }}>
                                <BoltIcon fontSize="inherit" />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Smarter Tools & Real-Time Insights
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Market trends, buyer behaviour, and pricing data to boost
                                    performance.
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* ITEM 4 */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box sx={{ fontSize: 40, color: theme.palette.success.main }}>
                                <VerifiedIcon fontSize="inherit" />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Safe, Transparent & Easy to Use
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Structured verification for dealers and listings. Clear,
                                    accurate, and trustworthy.
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* ITEM 5 */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box sx={{ fontSize: 40, color: theme.palette.secondary.main }}>
                                <HandshakeIcon fontSize="inherit" />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Growing the Industry Together
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tools and resources that support collaboration and long-term
                                    industry growth.
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 6 }} />

                {/* COMMITMENT */}
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Our Commitment
                </Typography>
                <Typography variant="body1" fontStyle="italic" color="text.secondary">
                    We aim to be Singapore’s most trusted platform for commercial vehicles
                    by connecting the right buyers with the right vehicles — efficiently,
                    transparently, and professionally.
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={2}>
                    Our mission is clear: to simplify and elevate the entire commercial
                    vehicle journey — from listing to closing the deal.
                </Typography>

                <Divider sx={{ my: 6 }} />

                {/* WHY WORKHORSE */}
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Why WorkHorse?
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={2}>
                    WorkHorse focuses 100% on commercial vehicles, meaning every feature of
                    our platform is purpose-built for the industry.
                    <br />
                    Dealers gain stronger visibility, higher-quality enquiries, and smart
                    tools to close deals faster, while buyers enjoy a transparent, trusted
                    environment to find the right vehicle with confidence.
                </Typography>
            </Container>

            <Footer />
        </>
    );
};

export default AboutPage;
